import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { useUpdateCarrierTrip } from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog, type DialogProps } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface EditTripColorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tripId: string;
  initialColor: string | null;
  dialogPresentation?: DialogProps["presentation"];
}

/**
 * Edit a trip's display color — `PATCH /trips/:id`.
 */
export function EditTripColorDialog({
  open,
  onOpenChange,
  tripId,
  initialColor,
  dialogPresentation,
}: EditTripColorDialogProps) {
  const update = useUpdateCarrierTrip();
  const [color, setColor] = useState<string>(initialColor || "#3b82f6");
  const submitLock = useRef(false);
  const responsive = dialogPresentation === "responsive-sheet";

  useEffect(() => {
    if (open) setColor(initialColor || "#3b82f6");
  }, [open, initialColor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitLock.current) return;
    submitLock.current = true;
    try {
      await update.mutateAsync({ tripId, color });
      toast.success("Trip color updated.");
      onOpenChange(false);
    } catch {
      toast.error("Failed to update color.");
      // also surfaced via update.isError below
    } finally {
      submitLock.current = false;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!update.isPending) onOpenChange(nextOpen);
      }}
      title="Edit trip color"
      presentation={dialogPresentation}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="edit-trip-color"
            className="block text-sm font-medium mb-1"
          >
            Color
          </label>
          <Input
            id="edit-trip-color"
            type="color"
            className="w-20 p-1 md:w-16"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            disabled={update.isPending}
          />
        </div>

        {update.isError && (
          <p role="alert" className="text-sm text-destructive">
            Failed to update color. Try again.
          </p>
        )}

        <div
          className={
            responsive
              ? "sticky bottom-0 grid grid-cols-2 gap-2 border-t bg-background pt-4"
              : "flex justify-end gap-2"
          }
        >
          <Button
            type="button"
            variant="outline"
            size={responsive ? "touch" : "default"}
            onClick={() => onOpenChange(false)}
            disabled={update.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size={responsive ? "touch" : "default"}
            disabled={update.isPending}
          >
            {update.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
